import { createJSONStorage, type StateStorage } from 'zustand/middleware'
import { toast } from 'react-hot-toast'
import i18n from '@/i18n'
import type { ColumnDefaults, Theme, Locale, ProjectVersion, ChangeLogEntry, Table, Relation, Groups } from '@/types'

const ROOT_STORAGE_KEY = 'erd_editor_v5'
const SETTINGS_STORAGE_KEY = 'morpherd_settings'
const VERSIONS_STORAGE_KEY = 'morpherd_versions'
const SAVE_DEBOUNCE_MS = 800

type PersistedDiagramSlice = {
  tables?: Table[]
  rels?: Relation[]
  groups?: Groups
  defaults?: ColumnDefaults
  projectName?: string
}

type PersistedSettingsSlice = {
  themePreference?: Theme
  language?: Locale
  showGrid?: boolean
  relationshipAnimationEnabled?: boolean
  defaultLineMode?: 'curved' | 'orthogonal' | 'straight'
  sidebarHidden?: boolean
  sbCollapsed?: Record<string, boolean>
  defaults?: ColumnDefaults
}

type PersistedVersionsSlice = {
  currentVersionId?: string | null
  projectVersions?: ProjectVersion[]
  changeLog?: ChangeLogEntry[]
}

type LegacyEditorState = PersistedDiagramSlice &
  PersistedVersionsSlice & {
    metadata?: Record<string, unknown> & { locale?: Locale }
    defaults?: ColumnDefaults
    defaultLineMode?: 'ortho' | 'orthogonal' | 'curved' | 'straight'
    relAnimation?: boolean
    theme?: Theme
    grid?: boolean
    sidebarHidden?: boolean
    sbCollapsed?: Record<string, boolean>
  }

type StoreNamespace = 'diagram' | 'settings' | 'versions'

let pendingRoot: LegacyEditorState | null = null
let flushTimer: ReturnType<typeof setTimeout> | null = null
let flushHandlersBound = false

function parseJson<T>(raw: string | null): T | null {
  if (!raw) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function parsePersistPayload<T>(raw: string | null): T | null {
  const parsed = parseJson<{ state?: T } & T>(raw)
  if (!parsed) return null
  return parsed.state ?? parsed
}

function normalizeLineMode(
  value: LegacyEditorState['defaultLineMode'] | PersistedSettingsSlice['defaultLineMode'] | undefined,
): PersistedSettingsSlice['defaultLineMode'] {
  if (value === 'curved' || value === 'straight') return value
  return 'orthogonal'
}

function readLocale(value: unknown): Locale | undefined {
  return value === 'pt-BR' || value === 'en-US' ? value : undefined
}

function readCombinedRoot(): LegacyEditorState {
  const rootState = parsePersistPayload<LegacyEditorState>(
    window.localStorage.getItem(ROOT_STORAGE_KEY),
  ) ?? {}
  const settingsState = parsePersistPayload<PersistedSettingsSlice>(
    window.localStorage.getItem(SETTINGS_STORAGE_KEY),
  )
  const versionsState = parsePersistPayload<PersistedVersionsSlice>(
    window.localStorage.getItem(VERSIONS_STORAGE_KEY),
  )

  const metadata = { ...(rootState.metadata ?? {}) }

  if (settingsState?.language) {
    metadata.locale = settingsState.language
  }

  return {
    ...rootState,
    ...(settingsState
      ? {
          defaults: settingsState.defaults ?? rootState.defaults,
          defaultLineMode: normalizeLineMode(settingsState.defaultLineMode),
          relAnimation:
            settingsState.relationshipAnimationEnabled ?? rootState.relAnimation,
          theme: settingsState.themePreference ?? rootState.theme,
          grid: settingsState.showGrid ?? rootState.grid,
          sidebarHidden: settingsState.sidebarHidden ?? rootState.sidebarHidden,
          sbCollapsed: settingsState.sbCollapsed ?? rootState.sbCollapsed,
          metadata,
        }
      : {}),
    ...(versionsState
      ? {
          currentVersionId:
            versionsState.currentVersionId ?? rootState.currentVersionId ?? null,
          projectVersions:
            versionsState.projectVersions ?? rootState.projectVersions ?? [],
          changeLog: versionsState.changeLog ?? rootState.changeLog ?? [],
        }
      : {}),
  }
}

function notifyQuotaError(error: unknown): void {
  const message =
    error instanceof Error ? error.message : i18n.t('common.unknown')
  console.warn('Persistência local falhou:', error)
  toast.error(i18n.t('common.browserSaveFailed', { message }), {
    id: 'localstorage-quota-error',
  })
}

function flushRoot(): void {
  if (!pendingRoot) return

  try {
    window.localStorage.setItem(ROOT_STORAGE_KEY, JSON.stringify(pendingRoot))
  } catch (error) {
    notifyQuotaError(error)
  } finally {
    pendingRoot = null
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
  }
}

function ensureFlushBindings(): void {
  if (flushHandlersBound || typeof window === 'undefined') return
  flushHandlersBound = true

  window.addEventListener('pagehide', flushRoot)
  window.addEventListener('beforeunload', flushRoot)
}

function scheduleFlush(): void {
  ensureFlushBindings()

  if (flushTimer) {
    clearTimeout(flushTimer)
  }

  flushTimer = setTimeout(() => {
    flushRoot()
  }, SAVE_DEBOUNCE_MS)
}

function toSettingsSlice(root: LegacyEditorState): PersistedSettingsSlice {
  return {
    themePreference: root.theme ?? 'system',
    language: readLocale(root.metadata?.locale) ?? 'pt-BR',
    showGrid: root.grid ?? true,
    relationshipAnimationEnabled: root.relAnimation ?? true,
    defaultLineMode: normalizeLineMode(root.defaultLineMode),
    sidebarHidden: root.sidebarHidden ?? false,
    sbCollapsed: root.sbCollapsed ?? {},
    defaults: root.defaults,
  }
}

function toVersionsSlice(root: LegacyEditorState): PersistedVersionsSlice {
  return {
    currentVersionId: root.currentVersionId ?? null,
    projectVersions: root.projectVersions ?? [],
    changeLog: root.changeLog ?? [],
  }
}

function buildPersistPayload(namespace: StoreNamespace, root: LegacyEditorState): string {
  if (namespace === 'diagram') {
    const state: PersistedDiagramSlice = {
      tables: root.tables ?? [],
      rels: root.rels ?? [],
      groups: root.groups ?? {},
      defaults: root.defaults,
      projectName: root.projectName ?? 'MorphERD',
    }
    return JSON.stringify({ state, version: 0 })
  }

  if (namespace === 'settings') {
    return JSON.stringify({ state: toSettingsSlice(root), version: 0 })
  }

  return JSON.stringify({ state: toVersionsSlice(root), version: 0 })
}

function mergeNamespaceState(
  namespace: StoreNamespace,
  root: LegacyEditorState,
  state: Record<string, unknown>,
): LegacyEditorState {
  if (namespace === 'diagram') {
    return {
      ...root,
      tables: (state.tables as Table[] | undefined) ?? root.tables ?? [],
      rels: (state.rels as Relation[] | undefined) ?? root.rels ?? [],
      groups: (state.groups as Groups | undefined) ?? root.groups ?? {},
      defaults: (state.defaults as ColumnDefaults | undefined) ?? root.defaults,
      projectName:
        (state.projectName as string | undefined) ?? root.projectName ?? 'MorphERD',
    }
  }

  if (namespace === 'settings') {
    const nextLocale = (state.language as Locale | undefined) ?? root.metadata?.locale
    return {
      ...root,
      defaults: (state.defaults as ColumnDefaults | undefined) ?? root.defaults,
      defaultLineMode: normalizeLineMode(
        state.defaultLineMode as PersistedSettingsSlice['defaultLineMode'] | undefined,
      ),
      relAnimation:
        (state.relationshipAnimationEnabled as boolean | undefined) ??
        root.relAnimation ??
        true,
      theme: (state.themePreference as Theme | undefined) ?? root.theme ?? 'system',
      grid: (state.showGrid as boolean | undefined) ?? root.grid ?? true,
      sidebarHidden:
        (state.sidebarHidden as boolean | undefined) ??
        root.sidebarHidden ??
        false,
      sbCollapsed:
        (state.sbCollapsed as Record<string, boolean> | undefined) ??
        root.sbCollapsed ??
        {},
      metadata: {
        ...(root.metadata ?? {}),
        ...(nextLocale ? { locale: nextLocale } : {}),
      },
    }
  }

  return {
    ...root,
    currentVersionId:
      (state.currentVersionId as string | null | undefined) ??
      root.currentVersionId ??
      null,
    projectVersions:
      (state.projectVersions as ProjectVersion[] | undefined) ??
      root.projectVersions ??
      [],
    changeLog:
      (state.changeLog as ChangeLogEntry[] | undefined) ?? root.changeLog ?? [],
  }
}

function createNamespacedStorage(namespace: StoreNamespace): StateStorage {
  return {
    getItem: () => buildPersistPayload(namespace, pendingRoot ?? readCombinedRoot()),
    setItem: (_name, value) => {
      const payload = parseJson<{ state?: Record<string, unknown> }>(value)
      const nextState = payload?.state
      if (!nextState) return

      const baseRoot = pendingRoot ?? readCombinedRoot()
      pendingRoot = mergeNamespaceState(namespace, baseRoot, nextState)
      scheduleFlush()
    },
    removeItem: () => {
      const baseRoot = pendingRoot ?? readCombinedRoot()

      if (namespace === 'diagram') {
        pendingRoot = {
          ...baseRoot,
          tables: [],
          rels: [],
          groups: {},
          projectName: 'MorphERD',
        }
      } else if (namespace === 'settings') {
        pendingRoot = {
          ...baseRoot,
          defaults: undefined,
          defaultLineMode: 'orthogonal',
          relAnimation: true,
          theme: 'system',
          grid: true,
          sidebarHidden: false,
          sbCollapsed: {},
          metadata: {
            ...(baseRoot.metadata ?? {}),
            locale: 'pt-BR',
          },
        }
      } else {
        pendingRoot = {
          ...baseRoot,
          currentVersionId: null,
          projectVersions: [],
          changeLog: [],
        }
      }

      scheduleFlush()
    },
  }
}

export const diagramStorage = createJSONStorage<PersistedDiagramSlice>(() =>
  createNamespacedStorage('diagram'),
)

export const settingsStorage = createJSONStorage<PersistedSettingsSlice>(() =>
  createNamespacedStorage('settings'),
)

export const versionsStorage = createJSONStorage<PersistedVersionsSlice>(() =>
  createNamespacedStorage('versions'),
)

export function flushPersistedStorage(): void {
  flushRoot()
}
